import type { AnalyticsAction } from "@asurion-core/eventualize";
import type { ExtractProps, WithClickAnalyticsProps } from "@asurion-core/eventualize-react";
import type { ComponentPropsWithRef, ElementType } from "react";

import { clickAnalyticsHocBody } from "@asurion-core/eventualize-react";

import type { SalesAnalyticsDispatcher } from "../../sales-context/SalesContext.types";
import type { MantineComponentProps, MantineDefaultElementType, MantineDefaultRef } from "./types";

import { useSalesContext } from "../../sales-context/SalesContext";

type Props<TComponentProps, TAction extends AnalyticsAction, TElementType extends ElementType, TRef> = TComponentProps &
    Omit<ComponentPropsWithRef<TElementType>, keyof TComponentProps> &
    WithClickAnalyticsProps<TAction, TComponentProps, TRef>;

/** Augments the provided component with click analytics */
export function withPolymorphicClickAnalytics<
    TComponent,
    TAction extends AnalyticsAction,
    TComponentProps = ExtractProps<TComponent>,
    TElementType extends ElementType = MantineDefaultElementType<TComponentProps>,
    TRefType = MantineDefaultRef<TComponentProps>,
>(Component: TComponent, action: TAction) {
    return clickAnalyticsHocBody<
        TComponent,
        TAction,
        TRefType,
        MantineComponentProps<TComponentProps>,
        Props<MantineComponentProps<TComponentProps>, TAction, TElementType, TRefType>
    >(Component, action);
}

function createNoopDispatcher(): SalesAnalyticsDispatcher {
    const dispatcher: SalesAnalyticsDispatcher = {
        dispatchBusinessEvent: () => Promise.resolve(),
        dispatchAction: () => Promise.resolve(),
        dispatchUserAction: () => Promise.resolve(),
        withExtra: () => dispatcher,
        withMeta: () => dispatcher,
        withIdentity: () => dispatcher,
        withIdentities: () => dispatcher,
    };
    return dispatcher;
}

let _rootDispatcher: SalesAnalyticsDispatcher | null = null;

/**
 * Returns the analytics dispatcher from SalesContext.
 * Also caches a reference for getRootDispatcher (non-hook contexts).
 */
export function useAnalytics() {
    const { analytics } = useSalesContext();
    const dispatcher = analytics.dispatcher;
    _rootDispatcher = dispatcher;
    return { dispatcher };
}

/**
 * Returns a cached dispatcher reference for use outside React hooks.
 * Falls back to a noop dispatcher if not yet initialized.
 */
export function getRootDispatcher(): SalesAnalyticsDispatcher {
    return _rootDispatcher ?? createNoopDispatcher();
}
